/*
 * InvisiBra sizing logic, shared by the size chart, calculator, product popup and size helper.
 *
 * The data comes from snippets/invisibra-size-data.liquid, which the chart snippet prints as
 * <script type="application/json" data-invisibra-size-data>. Several sections can print it on one page,
 * so it's found by attribute, not id; every copy is identical. Nothing in this file holds sizing data
 * apart from the standard UK -> US/EU/FR/AU label conversions.
 *
 * Runs in the browser (window.InvisiSizing) and in Node (module.exports) for tools/test-sizing.js.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  } else {
    root.InvisiSizing = api;
  }
})(typeof self !== 'undefined' ? self : this, () => {
  // UK cup scale. Index = inches of bust over band (0 = AA ... 10 = GG).
  const CUPS = ['AA', 'A', 'B', 'C', 'D', 'DD', 'E', 'F', 'FF', 'G', 'GG'];

  // Relabelling only: the InvisiBra grid is identical in every region.
  const EU_CUPS = ['AA', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];
  const REGIONS = {
    UK: { name: 'UK', band: (b) => b, cups: CUPS },
    US: { name: 'US', band: (b) => b, cups: ['AA', 'A', 'B', 'C', 'D', 'DD', 'DDD', 'G', 'H', 'I', 'J'] },
    EU: { name: 'EU', band: (b) => 60 + (b - 28) * 2.5, cups: EU_CUPS },
    FR: { name: 'FR/ES/BE', band: (b) => 75 + (b - 28) * 2.5, cups: EU_CUPS },
    AU: { name: 'AU/NZ', band: (b) => b - 22, cups: CUPS },
  };

  const CM_PER_INCH = 2.54;

  // Math.round rounds -0.5 towards +inf and suffers float noise (e.g. 4.4999999). Be explicit.
  const roundHalfUp = (x) => Math.floor(x + 0.5 + 1e-9);

  /*
   * Band = snug underbust rounded to the nearest even inch. Odd whole numbers are exactly
   * halfway, and round UP (27 -> 28, 33 -> 34), matching the old calculator's data.
   * 32.9 -> 32, 33.0 -> 34.
   */
  const bandFromUnderbust = (underbust) => 2 * roundHalfUp(underbust / 2);

  const parse = (source) => {
    if (!source) return null;
    const raw = typeof source === 'string' ? source : source.textContent;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  };

  const fromPage = (root) => parse((root || document).querySelector('script[data-invisibra-size-data]'));

  const bandsIn = (data) => Object.keys(data.bands).map(Number).sort((a, b) => a - b);

  const ukLabel = (band, cupIndex) => `${band}${CUPS[cupIndex]}`;

  const regionLabel = (band, cupIndex, region) => {
    const r = REGIONS[region] || REGIONS.UK;
    return `${r.band(band)}${r.cups[cupIndex]}`;
  };

  /*
   * Per-measurement checks for the stepper. Ranges are plausible human measurements in inches
   * (cm derived and rounded). Numbers inside them but off the chart still get a friendly result.
   */
  const FIELD_RANGES_IN = { underbust: [20, 55], standing: [22, 65], leaning: [22, 65], lying: [22, 65] };

  const fieldRange = (field, unit) => {
    const [min, max] = FIELD_RANGES_IN[field];
    return unit === 'cm' ? [Math.round(min * CM_PER_INCH), Math.round(max * CM_PER_INCH)] : [min, max];
  };

  // null for an empty box, NaN for anything that isn't a plain number ("32in", "abc").
  const parseMeasurement = (raw) => {
    if (raw === null || raw === undefined) return null;
    const text = String(raw).trim().replace(',', '.');
    if (text === '') return null;
    const n = Number(text);
    return Number.isFinite(n) ? n : NaN;
  };

  // 'empty' | 'range' | 'smaller' (a bust measurement below the underbust) | null when fine.
  const checkField = (field, raw, unit, underbustRaw) => {
    const n = parseMeasurement(raw);
    if (n === null) return 'empty';
    const [min, max] = fieldRange(field, unit);
    if (Number.isNaN(n) || n < min || n > max) return 'range';
    if (field !== 'underbust') {
      const underbust = parseMeasurement(underbustRaw);
      if (underbust !== null && !Number.isNaN(underbust) && n < underbust) return 'smaller';
    }
    return null;
  };

  // The InvisiBra size for a UK band + cup index, or null when the chart has no recommendation.
  const lookup = (data, band, cupIndex) => {
    const row = data.bands[String(band)];
    if (!row) return null;
    const size = row[cupIndex];
    return size && size !== '-' ? size : null;
  };

  const sizeLabel = (data, size) => {
    const name = (data.names && data.names[size]) || size;
    return `${data.label_prefix || 'InvisiBra Size'} ${name}`;
  };

  /*
   * In-between results such as "D/E" (between InvisiBra Size D and Size E). The page's rule is
   * "go down one", so lower is the recommendation. Some styles sell the in-between as its own
   * size (data.between, e.g. D/E -> DD): that's `product`.
   */
  const betweenInfo = (data, size) => {
    if (!size || size.indexOf('/') === -1) return null;
    const [lower, upper] = size.split('/');
    const product = (data.between && data.between[size]) || null;
    return {
      lower,
      upper,
      lowerLabel: sizeLabel(data, lower),
      upperLabel: sizeLabel(data, upper),
      product,
      productLabel: product ? sizeLabel(data, product) : null,
    };
  };

  // Fuller-bust guidance: limits map a cup to the largest band InvisiBra suits.
  const isAboveLimit = (data, band, cupIndex) => {
    const limit = data.limits && data.limits[CUPS[cupIndex]];
    return typeof limit === 'number' && band > limit;
  };

  /*
   * Every UK (or regional) size that wears a given InvisiBra size, smallest band first.
   * An in-between cell (D/E) counts for its lower size (go down one) and its product size (DD).
   * Pass `sold` (the sizes one product comes in) to pick one: the product size if the product sells
   * it, otherwise the lower size. The product page's Liquid (invisibra-size-chart, part 'fits')
   * does the same.
   */
  const fitsFor = (data, size, region, sold) => {
    const fits = [];
    bandsIn(data).forEach((band) => {
      data.bands[String(band)].forEach((cell, cupIndex) => {
        const between = betweenInfo(data, cell);
        let matches = cell === size;
        if (between && !sold) matches = between.lower === size || between.product === size;
        if (between && sold) matches = between.product && sold.includes(between.product) ? between.product === size : between.lower === size;
        if (matches) fits.push(regionLabel(band, cupIndex, region || 'UK'));
      });
    });
    return fits;
  };

  /*
   * Usual UK bra size -> the InvisiBra size to pick on one product (the product-page finder,
   * assets/invisibra-size-finder.js). `sold` is the sizes the product comes in, e.g. ['A','B','C','D'].
   *   no-size    the chart has no recommendation for this size
   *   not-sold   the chart gives a size, but this product doesn't come in it
   *   ok         `size` is one of `sold`
   * An in-between cell (D/E) is the product's own size (DD) when it sells it; otherwise it's the lower
   * size, and `between` is set so the page can say it went down one. aboveLimit: past the fuller-bust limit.
   */
  const forBraSize = (data, band, cupIndex, sold) => {
    const uk = ukLabel(band, cupIndex);
    const aboveLimit = isAboveLimit(data, band, cupIndex);
    const cell = lookup(data, band, cupIndex);
    if (!cell) return { status: 'no-size', uk, aboveLimit };

    let size = cell;
    let between = null;
    const info = betweenInfo(data, cell);
    if (info) {
      if (info.product && sold.includes(info.product)) {
        size = info.product;
      } else {
        size = info.lower;
        between = info;
      }
    }
    return { status: sold.includes(size) ? 'ok' : 'not-sold', size, sizeLabel: sizeLabel(data, size), uk, aboveLimit, between };
  };

  /*
   * Measurements -> result. Every outcome has a status so the page can show a real message,
   * never a bare "-".
   *   incomplete           one or more fields empty
   *   invalid              not a positive number, or wildly out of human range
   *   bust-smaller         average bust is smaller than the underbust
   *   band-out-of-range    band outside the chart
   *   cup-out-of-range     cup above GG
   *   no-size              the chart has no recommendation for this size
   *   ok                   size found (check aboveLimit for the fuller-bust message, and
   *                        between for in-between results such as D/E)
   */
  const fromMeasurements = (data, input) => {
    const unit = input.unit === 'cm' ? 'cm' : 'in';
    const fields = ['underbust', 'standing', 'leaning', 'lying'];
    const values = {};

    for (const field of fields) {
      const raw = input[field];
      if (raw === '' || raw === null || raw === undefined) return { status: 'incomplete' };
      const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
      if (!isFinite(n) || n <= 0) return { status: 'invalid', field };
      values[field] = unit === 'cm' ? n / CM_PER_INCH : n;
    }

    // Anything outside 20-70 in is a typo or the wrong unit, not a body.
    for (const field of fields) {
      if (values[field] < 20 || values[field] > 70) return { status: 'invalid', field };
    }

    const bust = (values.standing + values.leaning + values.lying) / 3;
    if (bust < values.underbust) return { status: 'bust-smaller' };

    const band = bandFromUnderbust(values.underbust);
    const bands = bandsIn(data);
    const result = { band, bust, underbust: values.underbust };

    if (band < bands[0] || band > bands[bands.length - 1]) {
      return Object.assign(result, { status: 'band-out-of-range' });
    }

    // Bust >= underbust and band <= underbust + 1, so bust - band >= -1: that's still AA, not off the scale.
    const cupIndex = Math.max(0, roundHalfUp(bust - band));
    if (cupIndex >= CUPS.length) {
      return Object.assign(result, { status: 'cup-out-of-range', cupIndex });
    }

    Object.assign(result, {
      cupIndex,
      cup: CUPS[cupIndex],
      uk: ukLabel(band, cupIndex),
      aboveLimit: isAboveLimit(data, band, cupIndex),
    });

    const size = lookup(data, band, cupIndex);
    if (!size) return Object.assign(result, { status: 'no-size' });

    return Object.assign(result, { status: 'ok', size, sizeLabel: sizeLabel(data, size), between: betweenInfo(data, size) });
  };

  return {
    CUPS,
    REGIONS,
    CM_PER_INCH,
    roundHalfUp,
    bandFromUnderbust,
    parse,
    fromPage,
    bandsIn,
    ukLabel,
    regionLabel,
    lookup,
    sizeLabel,
    betweenInfo,
    FIELD_RANGES_IN,
    fieldRange,
    parseMeasurement,
    checkField,
    isAboveLimit,
    fitsFor,
    forBraSize,
    fromMeasurements,
  };
});